'use client';

import { useState, useEffect } from 'react';
import { useRouter } from 'next/navigation';
import { useForm, SubmitHandler } from 'react-hook-form';
import Link from "next/link";
import {
  TextField,
  Button,
  Alert,
} from '@mui/material';
import authenticationApi from '@/lib/authenticationApi';
import AuthLayout from '@/components/AuthLayout';
import { getCookie } from 'cookies-next';
import { deleteAuthCookie } from '@/lib/authCookies';

type ForgotPasswordFormData = {
  email: string;
};

type ResetPasswordFormData = {
  resetCode: string;
  newPassword: string;
  confirmPassword: string;
};

export default function ForgotPassword() {
  const router = useRouter();
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false); // Tracks if reset code was sent
  const [message, setMessage] = useState(''); // Tracks if reset code was sent
  const [resetEmail, setResetEmail] = useState(''); // Store email for reset
  const [isSubmitting, setIsSubmitting] = useState(false);
  const lockedEmail = getCookie('locked_email');

  // Remove locked_email cookie when component mounts
  useEffect(() => {
    document.title = 'CareAscendium - Forgot Password';
    deleteAuthCookie('locked_email');
  }, []);

  // Forgot password form (request reset code)
  const {
    register: registerForgot,
    handleSubmit: handleSubmitForgot,
    formState: { errors: forgotErrors, isSubmitting: isSubmittingForgot },
  } = useForm<ForgotPasswordFormData>({
    defaultValues: { email: (lockedEmail as string) || '' },
  });

  // Handle forgot password submission (request reset code)
  const onForgotSubmit: SubmitHandler<ForgotPasswordFormData> = async (data) => {
    setError('');
    setIsSubmitting(true);
    await authenticationApi.post(`/v1/passwords/new`, { email: data.email })
    .then((response: any) => {
      setIsSubmitting(false);
      setResetEmail(data.email);
      setSuccess(true);
      setMessage(response.data.message);
    })
    .catch((error: any) => {
      if(error.status != 500) {
        setError(error.details.message);
      } else {
        setError('An unexpected error occurred. Please try again later.');
      }
      setIsSubmitting(false);
    })
  };

  return (
    <AuthLayout
      title=""
      welcomeTitle="Forgot your password?"
      leftBackgroundImage="/login-bg.jpg"
      darkTheme
      logoImage="/auth-white-logo.png"
      tagline="QUALITY. PRODUCTIVITY. COMPOUNDED."
      belowCardContent={
        <p className="text-white/90 text-sm font-normal">
          <Link
            href="/login"
            className="text-sky-400 hover:text-sky-300 font-medium underline"
          >
            Back to Login
          </Link>
        </p>
      }
    >
      {message && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {message}
        </Alert>
      )}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      <form onSubmit={handleSubmitForgot(onForgotSubmit)} className="space-y-4 sm:space-y-6">
        <div>
          <label className="block text-sm font-semibold text-white/90 mb-1.5">
            Email *
          </label>
          <TextField
            {...registerForgot('email', {
              required: 'Email is required',
              pattern: {
                value: /\S+@\S+\.\S+/,
                message: 'Please enter a valid email address',
              },
            })}
            fullWidth
            variant="outlined"
            placeholder="johnsmith@gmail.com"
            error={!!forgotErrors.email}
            helperText={forgotErrors.email?.message}
            InputLabelProps={{ shrink: true }}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: '6px',
                fontSize: '1rem',
                backgroundColor: '#fff',
                color: '#1e293b',
                '& fieldset': { borderColor: '#cbd5e1' },
                '&:hover fieldset': { borderColor: '#94a3b8' },
                '&.Mui-focused fieldset': {
                  borderWidth: '2px',
                  borderColor: '#153C7F',
                },
              },
              '& .MuiFormHelperText-root': {
                color: '#fca5a5',
                marginTop: '4px',
              },
            }}
          />
        </div>
        <Button
          type="submit"
          variant="contained"
          fullWidth
          disabled={isSubmittingForgot}
          sx={{
            py: { xs: 2, sm: 1.75 },
            mt: 0.5,
            borderRadius: '6px',
            backgroundColor: '#63A821',
            color: '#fff',
            fontWeight: 600,
            fontSize: '1rem',
            textTransform: 'none',
            boxShadow: '0 2px 12px rgba(99,168,33,0.35)',
            '&:hover': {
              backgroundColor: '#4e8c1a',
              boxShadow: '0 4px 14px rgba(99,168,33,0.4)',
            },
            '&.Mui-disabled': {
              backgroundColor: '#63A821',
              color: '#fff',
              opacity: 0.9,
            },
          }}
        >
          {isSubmittingForgot ? 'Sending Instructions...' : 'Send Instructions'}
        </Button>
      </form>
    </AuthLayout>
  );
}